import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Card, CardContent, Button, Grid } from '@mui/material';
import { useUser } from '../contexts/UserContext';
import { playerService, Player, FantasyTeam } from '../services/playerService';
import PlayerCard from '../components/PlayerCard';
import PlayerStats from '../components/PlayerStats';
import PlayerFilters from '../components/PlayerFilters';
import PlayerSearch from '../components/PlayerSearch';

const Players2Page: React.FC = () => {
  const { currentUsername, isUserSwitching } = useUser();
  
  // Data loading state
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<string[]>([]);
  const [userFantasyTeams, setUserFantasyTeams] = useState<FantasyTeam[]>([]);
  const [userPlayers, setUserPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    position: 'All',
    team: 'All',
    minFantasyPoints: 0,
    maxFantasyPoints: 100,
    availability: 'Free Agent' // Default to showing only free agent players
  });
  const [ownedPlayerIds, setOwnedPlayerIds] = useState<Set<number>>(new Set());



  // Load data function
  const loadData = useCallback(async () => {
    if (!currentUsername) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Players2Page: Loading data...');
      
      // Load user and fantasy teams
      const user = await playerService.getUserByUsername(currentUsername);
      const fantasyTeams = await playerService.getFantasyTeamsByUser(user.id);
      setUserFantasyTeams(fantasyTeams);
      
      // Load all players, teams, and owned player IDs
      const [allPlayers, allTeams, ownedIds] = await Promise.all([
        playerService.getAllPlayers(),
        playerService.getTeams(),
        playerService.getOwnedPlayerIds().catch(() => []) // Fallback to empty array if fails
      ]);
      
      setPlayers(allPlayers);
      setTeams(allTeams.map(t => t.name));
      setOwnedPlayerIds(new Set(ownedIds));
      
              // Get user's team players for highlighting
        if (fantasyTeams.length > 0) {
          try {
            const teamPlayers = await playerService.getTeamPlayers(fantasyTeams[0].id);
            setUserPlayers(teamPlayers.map(ftp => ftp.player));
        } catch (err) {
          console.warn('Could not load team players, setting empty array');
          setUserPlayers([]);
        }
      }
      
      console.log('✅ Players2Page: Data loaded successfully');
      
    } catch (err) {
      console.error('❌ Players2Page: Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUsername]);

  // Load data on mount and user change
  useEffect(() => {
    if (currentUsername && !isUserSwitching) {
      loadData();
    }
  }, [currentUsername, isUserSwitching, loadData]);

  // Filter and search players
  const filteredPlayers = players
    .filter(player => {
      // Availability filter
      const isOwned = ownedPlayerIds.has(player.id);
      let matchesAvailability = true;
      if (filters.availability === 'Free Agent') {
        matchesAvailability = !isOwned; // Show players NOT on a team
      } else if (filters.availability === 'Rostered') {
        matchesAvailability = isOwned; // Show players ON a team
      } else if (filters.availability === 'All') {
        matchesAvailability = true; // Show all players
      }
      
      // Search filter
      const matchesSearch = !searchTerm || 
        player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.team.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.position.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Position filter
      const matchesPosition = filters.position === 'All' || player.position === filters.position;
      
      // Team filter
      const matchesTeam = filters.team === 'All' || player.team === filters.team;
      
      // Fantasy points filter
      const matchesFantasyPoints = player.fantasyPointsPerGame >= filters.minFantasyPoints && 
                                  player.fantasyPointsPerGame <= filters.maxFantasyPoints;
      
      return matchesAvailability && matchesSearch && matchesPosition && matchesTeam && matchesFantasyPoints;
    })
    // Sort by fantasy points per game (descending - highest first)
    .sort((a, b) => (b.fantasyPointsPerGame || 0) - (a.fantasyPointsPerGame || 0));

  // Handle player selection
  const handlePlayerSelect = (player: Player) => {
    setSelectedPlayer(player);
  };

  // Handle add player
  const handleAddPlayer = async (player: Player) => {
    if (!primaryTeam) return;
    
    try {
      console.log('Adding player to team:', player.name);
      
      // Add player to team using the service
      await playerService.addPlayerToTeam(primaryTeam.id, player.id);
      
      // Update local state to reflect the change
      setUserPlayers(prev => [...prev, player]);
      
      // Show success message
      alert(`✅ ${player.name} added to ${primaryTeam.name}!`);
      
    } catch (error) {
      console.error('Error adding player to team:', error);
      alert('Failed to add player. Please try again.');
    }
  };

  // Handle remove player
  const handleRemovePlayer = async (player: Player) => {
    if (!userFantasyTeams.length) return;

    try {
      await playerService.removePlayerFromTeam(userFantasyTeams[0].id, player.id);
      setUserPlayers(prev => prev.filter(p => p.id !== player.id));
      alert(`✅ ${player.name} removed from ${userFantasyTeams[0].name}!`);
    } catch (err) {
      alert('Failed to remove player. Please try again.');
    }
  };

  // Show loading state while user is switching
  if (isUserSwitching) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Switching to {currentUsername}...</p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  const primaryTeam = userFantasyTeams[0];
  const isPlayerOnTeam = (player: Player) => userPlayers.some(up => up.id === player.id);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', p: 3 }}>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
          Players
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Browse and manage players with advanced filtering and statistics.
        </Typography>
        
        {primaryTeam && (
          <Box sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', borderRadius: 2, p: 2, mb: 3 }}>
            <Typography color="primary.800">
              Your team: <strong>{primaryTeam.name}</strong> ({userPlayers.length}/12 spots filled)
            </Typography>
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Left Sidebar - Filters */}
          <Grid size={{ xs: 12, md: 3 }}>
            <PlayerSearch 
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />
            
            <Box sx={{ mt: 3 }}>
              <PlayerFilters 
                filters={filters}
                onFilterChange={setFilters}
                teams={teams}
              />
            </Box>
          </Grid>

          {/* Main Content - Player Grid */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {filters.availability === 'Free Agent' 
                    ? 'Free Agents' 
                    : filters.availability === 'Rostered'
                    ? 'Rostered Players'
                    : 'All Players'} ({filteredPlayers.length})
                </Typography>
                
                {filteredPlayers.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No players found matching your criteria.
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {filteredPlayers.map((player) => {
                      return (
                        <Grid size={{ xs: 12, sm: 6 }} key={player.id}>
                          <PlayerCard
                            player={player}
                            onSelect={handlePlayerSelect}
                            isSelected={selectedPlayer?.id === player.id}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Sidebar - Player Stats */}
          <Grid size={{ xs: 12, md: 3 }}>
            <PlayerStats player={selectedPlayer} />
            
            {selectedPlayer && primaryTeam && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Team Status
                  </Typography>
                  
                  {isPlayerOnTeam(selectedPlayer) ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          display: 'inline-block',
                          bgcolor: 'success.100',
                          color: 'success.800',
                          px: 2,
                          py: 0.5,
                          borderRadius: 10,
                          fontSize: '0.875rem'
                        }}
                      >
                        ✓ On Your Team
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Click the player name to manage
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Click the player name to add to your team
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Players2Page;
